import { fsrs, generatorParameters, Rating, createEmptyCard, type CardInput } from 'ts-fsrs';
import type { TutorConcept } from '@/types/tutor';

export interface ScheduledConcept {
  srsState: unknown;
  dueDate: Date;
}

export function scheduleConceptReview(
  concept: TutorConcept,
  rating: Rating,
  now: Date,
): ScheduledConcept {
  const f = fsrs(generatorParameters());
  const cardState = (concept.srsState as CardInput) || createEmptyCard(now);
  const scheduling = f.repeat(cardState, now);
  const result = scheduling[rating as 1 | 2 | 3 | 4];
  return { srsState: result.card, dueDate: result.card.due };
}

export function computeMasteryLevel(currentLevel: number, rating: Rating): number {
  if (rating === Rating.Again) return Math.max(0, currentLevel - 1);
  if (rating === Rating.Hard) return currentLevel;
  if (rating === Rating.Good) return Math.min(3, currentLevel + 1);
  return Math.min(3, currentLevel + 2);
}

export function computeConceptStrength(concept: TutorConcept, now: Date): number {
  if (!concept.srsState) return 0;
  try {
    const f = fsrs(generatorParameters());
    const retrievability = f.get_retrievability(concept.srsState as CardInput, now, false);
    return Math.max(0, Math.min(1, retrievability));
  } catch {
    return 0;
  }
}

export function isConceptDue(concept: TutorConcept, now = new Date()): boolean {
  if (!concept.dueDate) return false;
  return new Date(concept.dueDate) <= now;
}

export function formatDueInterval(dueDate: string | null): string {
  if (!dueDate) return 'New';
  const due = new Date(dueDate);
  const now = new Date();
  const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Due now';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays < 7) return `Due in ${diffDays} days`;
  if (diffDays < 30) return `Due in ${Math.ceil(diffDays / 7)} weeks`;
  return `Due in ${Math.ceil(diffDays / 30)} months`;
}
